import { Aborter, AnonymousCredential, BlobURL, BlockBlobURL, ContainerURL, ServiceURL, StorageURL } from "@azure/storage-blob";
import JSZip from "jszip";
import Pica from "pica";
import { gapi } from "gapi-script";

import {
    decrementStorageUsedByTherapistInBytes,
    getAzureStorageSASToken,
    getTherapistAzureContainer,
    incrementStorageUsedByTherapistInBytes,
    setClientProfileImage
} from "../../actions";
import azureStorageEnvVar from "../../constants/AzureStorageEnvironmentVariable";

/**
 * Generates a sanitized filename by replacing non-alphanumeric characters with underscores and appending the current timestamp.
 * 
 * @param {string} filename - The original filename.
 * @returns {Object} An object containing the sanitized filename with and without the extension.
 * @property {string} blobObjectNameWithExtension - The sanitized filename with the file extension.
 * @property {string} blobObjectName - The sanitized filename without the file extension.
 */
export function generateSanitizedFilename(filename) {
    const [name, ...extensions] = filename.split(".");

    // Replace non-alphanumeric characters with underscores and convert to lowercase
    const sanitizedFilename = `${name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}-${Date.now()}`;

    return {
        blobObjectNameWithExtension: `${sanitizedFilename}.${extensions.join(".")}`, // Filename with extension
        blobObjectName: sanitizedFilename, // Filename without extension
    };
}

/**
 * Generates metadata for an Azure Blob based on the file properties.
 * 
 * @param {File} file - The file to generate metadata for.
 * @returns {Object} An object containing metadata properties such as name, type, and size.
 * @property {string} name - The original name of the file.
 * @property {string} type - The MIME type of the file.
 * @property {string} size - The size of the file in bytes, converted to a string.
 */
function generateMetadata(file) {
    return {
        name: file.name,
        type: file.type,
        size: file.size.toString() // Convert number to string as azure metadata expects a string
    };
}

/**
 * Resizes an image file to the specified width while preserving aspect ratio.
 * The height is automatically adjusted based on the original aspect ratio.
 * 
 * @param {File} file - The original image file.
 * @param {number} width - The desired width for the resized image.
 * @returns {Promise<File>} A promise that resolves to the resized image file.
 */
async function resizeImage(file, width) {
    try {
        const pica = Pica();
        const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.src = URL.createObjectURL(file);
            image.onload = () => resolve(image);
            image.onerror = reject;
        });

        // Calculate the height while preserving the aspect ratio
        const aspectRatio = img.height / img.width;
        const height = Math.round(width * aspectRatio);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        await pica.resize(img, canvas);
        const blob = await pica.toBlob(canvas, file.type);

        return new File([blob], file.name, { type: file.type });
    } catch (error) {
        console.error("Error in resizeImage:", error);
    }
}

/**
 * Fetches the SAS Token for the specified Azure Blob container and stores it in localStorage.
 * 
 * @param {string} containerName - The name of the Azure Blob container.
 * @param {string} localStorageKey - The key to store the SAS Token in localStorage.
 * @returns {Promise<string>} A promise that resolves to the SAS Token.
 */
async function fetchSASToken(containerName, localStorageKey) {
    try {
        // Fetch and store the SAS token for the specified Azure Blob container
        await getAzureStorageSASToken(containerName, localStorageKey);

        // Retrieve the stored token from localStorage
        const storedToken = localStorage.getItem(localStorageKey);
        if (storedToken) {
            return JSON.parse(storedToken).sasToken;
        } else {
            throw new Error("Failed to retrieve the SAS token from localStorage.");
        }
    } catch (error) {
        console.error("Error in fetchSASToken:", error);
    }
}

/**
 * Retrieves the SAS Token for an Azure Blob container, fetching a new one if the stored token is expired or missing.
 * 
 * @param {string} containerName - The name of the Azure Blob container.
 * @returns {Promise<string>} A promise that resolves to the SAS Token.
 */
export async function getSASToken(containerName) {
    try {
        const localStorageKey = "azure_token";
        const storedToken = localStorage.getItem(localStorageKey);

        if (storedToken) {
            const tokenData = JSON.parse(storedToken);

            // Check if the stored token is valid and not expired
            if (tokenData.sasToken && tokenData.expiresIn && Date.now() < tokenData.expiresIn) {
                return tokenData.sasToken;
            }
        }

        // Fetch a new SAS token if no valid token is found
        return await fetchSASToken(containerName, localStorageKey);
    } catch (error) {
        console.error("Error in getSASToken:", error);
    }
}

/**
 * Uploads a client's profile image to Azure Blob storage, resizes the image, and updates the client's storage usage.
 * 
 * @param {string} therapistId - The ID of the therapist who owns the container.
 * @param {string} clientId - The ID of the client.
 * @param {File} file - The image file to upload.
 * @returns {Promise<string>} A promise that resolves to the name of the uploaded image file.
 */
export async function uploadClientProfileImage(therapistId, clientId, file) {
    return uploadImage(therapistId, clientId, file, true);
}

/**
 * Deletes a client's profile image and its resized version from Azure Blob storage, and updates the client's storage usage.
 * 
 * @param {string} therapistId - The ID of the therapist who owns the container.
 * @param {string} clientId - The ID of the client.
 * @param {string} fileName - The name of the image file to delete.
 * @returns {Promise<boolean>} A promise that resolves to true if the deletion was successful.
 */
export async function deleteClientProfileImage(therapistId, clientId, fileName) {
    return deleteImages(therapistId, clientId, [fileName], true);
}

/**
 * Uploads multiple journal images to Azure Blob storage, resizes them, and updates the client's storage usage.
 * 
 * @param {string} therapistId - The ID of the therapist who owns the container.
 * @param {string} clientId - The ID of the client.
 * @param {File[]} files - An array of image files to upload.
 * @returns {Promise<string[]>} A promise that resolves to an array of uploaded image file names.
 */
export async function uploadClientJournalImages(therapistId, clientId, files) {
    const uploadedFiles = await Promise.all(files.map(file => uploadImage(therapistId, clientId, file)));
    return uploadedFiles;
}

/**
 * Deletes multiple journal images and their resized versions from Azure Blob storage, and updates the client's storage usage.
 * 
 * @param {string} therapistId - The ID of the therapist who owns the container.
 * @param {string} clientId - The ID of the client.
 * @param {string[]} fileNames - An array of image file names to delete.
 * @returns {Promise<boolean>} A promise that resolves to true if all deletions were successful.
 */
export async function deleteClientJournalImages(therapistId, clientId, fileNames) {
    return deleteImages(therapistId, clientId, fileNames);
}

/**
 * Helper function to get container URL and SAS token.
 * 
 * @param {string} therapistId - The ID of the therapist.
 * @param {string} clientId - The ID of the client.
 * @returns {Promise<{ containerURL: ContainerURL, sasToken: string }>} An object containing the container URL and SAS token.
 */
async function getContainerAndSasUrl(therapistId, clientId) {
    const { AZURE_STORAGE_URL, CLIENT_PREFIX } = azureStorageEnvVar;
    const therapistContainer = await getTherapistAzureContainer(therapistId);
    const sasToken = await getSASToken(therapistContainer);

    const pipeline = StorageURL.newPipeline(new AnonymousCredential());
    const serviceURL = new ServiceURL(`${AZURE_STORAGE_URL}?${sasToken}`, pipeline);
    const containerURL = ContainerURL.fromServiceURL(serviceURL, `${therapistContainer}/${CLIENT_PREFIX}_${clientId}`);

    return { containerURL, sasToken };
}

/**
 * Common function to upload an image and its resized version to Azure Blob storage.
 * 
 * @param {string} therapistId - The ID of the therapist.
 * @param {string} clientId - The ID of the client.
 * @param {File} file - The image file to upload.
 * @param {boolean} isProfileImage - Whether the file is a profile image.
 * @returns {Promise<string>} A promise that resolves to the name of the uploaded image file.
 */
async function uploadImage(therapistId, clientId, file, isProfileImage = false) {
    try {
        const { AZURE_STORAGE_URL, RESIZED_IMG_PREFIX, CLIENT_PREFIX } = azureStorageEnvVar;
        const { blobObjectNameWithExtension, blobObjectName } = generateSanitizedFilename(file.name);

        const { containerURL, sasToken } = await getContainerAndSasUrl(therapistId, clientId);
        const blockBlobURL = BlockBlobURL.fromBlobURL(BlobURL.fromContainerURL(containerURL, blobObjectNameWithExtension));

        // Upload the original image to Azure Blob storage
        await blockBlobURL.upload(Aborter.none, file, file.size, { metadata: generateMetadata(file) });

        // Resize the image and upload the resized image to Azure Blob storage
        const resizedFile = await resizeImage(file, 800);
        const resizedBlockBlobURL = BlockBlobURL.fromBlobURL(
            BlobURL.fromContainerURL(containerURL, `${RESIZED_IMG_PREFIX}_${blobObjectNameWithExtension}`)
        );
        await resizedBlockBlobURL.upload(Aborter.none, resizedFile, resizedFile.size, { metadata: generateMetadata(resizedFile) });

        // Update the client's storage usage
        const storageUsed = file.size + resizedFile.size;
        await incrementStorageUsedByTherapistInBytes(therapistId, storageUsed);

        // If the image is a profile image, update the client's profile image in the database
        if (isProfileImage) {
            await setClientProfileImage(blobObjectNameWithExtension, clientId);
        }

        return blobObjectNameWithExtension;
    } catch (error) {
        console.error("Error in uploadImage:", error);
        throw error;
    }
}

/**
 * Common function to delete images and their resized versions from Azure Blob storage.
 * 
 * @param {string} therapistId - The ID of the therapist.
 * @param {string} clientId - The ID of the client.
 * @param {string[]} fileNames - An array of image file names to delete.
 * @returns {Promise<boolean>} A promise that resolves to true if all deletions were successful.
 */
async function deleteImages(therapistId, clientId, fileNames, isProfileImage = false) {
    try {
        const { RESIZED_IMG_PREFIX } = azureStorageEnvVar;
        const { containerURL } = await getContainerAndSasUrl(therapistId, clientId);

        let totalStorageUsed = 0;

        // Delete all files and their resized versions from Azure Blob storage
        const deletePromises = fileNames.map(async (fileName) => {
            try {
                const blobURL = BlobURL.fromContainerURL(containerURL, fileName);
                const blobProperties = await blobURL.getProperties();
                await blobURL.delete(Aborter.none, { deleteSnapshots: "include" });

                const resizedBlobURL = BlobURL.fromContainerURL(containerURL, `${RESIZED_IMG_PREFIX}_${fileName}`);
                const resizedBlobProperties = await resizedBlobURL.getProperties();
                await resizedBlobURL.delete(Aborter.none, { deleteSnapshots: "include" });

                totalStorageUsed += blobProperties.contentLength + resizedBlobProperties.contentLength;
            } catch (error) {
                console.error(`Failed to delete file: ${fileName}`, error);
                throw error;
            }
        });

        await Promise.all(deletePromises);

        if (isProfileImage) {
            await setClientProfileImage('', clientId);
        }

        // Update the client's total storage usage
        await decrementStorageUsedByTherapistInBytes(therapistId, totalStorageUsed);

        return true;
    } catch (error) {
        console.error("Error in deleteImages:", error);
        return false;
    }
}

/**
 * Downloads multiple journal images from Azure Blob storage and packages them into a ZIP file for download.
 * 
 * @param {string} therapistId - The ID of the therapist who owns the container.
 * @param {string} clientId - The ID of the client.
 * @param {string[]} fileNames - An array of blob names to download.
 * @returns {Promise<boolean>} A promise that resolves to true if the ZIP file is successfully created and downloaded.
 */
export async function downloadJournalImagesAsZip(therapistId, clientId, fileNames, googleFiles) {
    try {
        const { AZURE_STORAGE_URL, CLIENT_PREFIX } = azureStorageEnvVar;

        const therapistContainer = await getTherapistAzureContainer(therapistId);
        const sasToken = await getSASToken(therapistContainer);

        const pipeline = StorageURL.newPipeline(new AnonymousCredential());
        const serviceURL = new ServiceURL(`${AZURE_STORAGE_URL}?${sasToken}`, pipeline);
        const containerURL = ContainerURL.fromServiceURL(serviceURL, `${therapistContainer}/${CLIENT_PREFIX}_${clientId}`);

        const zip = new JSZip();
        const zipFileName = "images.zip";

        // Download each image and add it to the ZIP file
        for (const fileName of fileNames) {
            const blobURL = BlobURL.fromContainerURL(containerURL, fileName);
            const downloadResponse = await blobURL.download(Aborter.none, 0);

            if (downloadResponse.blobBody) {
                const imageBlob = await downloadResponse.blobBody;
                zip.file(fileName, imageBlob);
            }
        }

        // Download images from Google Drive and add them to the ZIP file
        for (const file of googleFiles) {
            const { isFromGoogle, imageName, mimeType } = file;
            const response = await gapi.client.drive.files.get({
                fileId: isFromGoogle,
                alt: 'media',
            });

            const binary = response.body;
            const l = binary.length
            const array = new Uint8Array(l);
            for (let i = 0; i < l; i++) {
                array[i] = binary.charCodeAt(i);
            }

            // Convert the response body to a Blob and add to ZIP
            const googleImageBlob = new Blob([array], { type: mimeType });
            zip.file(imageName, googleImageBlob);
        }

        // Generate the ZIP file and trigger download
        const zipContent = await zip.generateAsync({ type: "blob", streamFiles: true });
        const url = URL.createObjectURL(zipContent);
        const a = document.createElement("a");
        a.href = url;
        a.download = zipFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return true; // Return true if the ZIP file is successfully created and downloaded
    } catch (error) {
        console.error("Error in downloadJournalImagesAsZip:", error);
        return false; // Return false if an error occurred during the process
    }
}
